// import Dots from '@mui/icons-material/MoreVeui/material';
import CategoryIcon from '@mui/icons-material/Category';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { GridColDef } from '@mui/x-data-grid';

import { getProductId, Product, WarehouseDetailRowState } from '@/types/fulfill/inventory';
import { formatDate } from '@/utils/common';

import KitThumbnailStack from './KitThumbnailStack';
import { mapWarehouseProducts } from './mapWarehouseProducts';
import WarehouseInventoryPanel from './WarehouseInventoryPanel';

const TOTAL_COLUMN_COUNT = 7;

interface InventoryColumnsProps {
  handleEditProduct: (product: Product) => void;
  handleViewKitComposition: (product: Product) => void;
  expandedRows: Record<string, WarehouseDetailRowState>;
  toggleWarehouseDetail: (product: Product) => void;
  finalizeWarehouseCollapse: (product: Product) => void;
}

export const inventoryColumns = ({
  handleEditProduct,
  handleViewKitComposition,
  expandedRows,
  toggleWarehouseDetail,
  finalizeWarehouseCollapse,
}: InventoryColumnsProps): Array<GridColDef> => [
  {
    field: 'thumbnail',
    flex: 0.5,
    headerName: `Producto`,
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    colSpan: (_value, row) => (row.isDetailRow ? TOTAL_COLUMN_COUNT : undefined),
    renderCell: (params) => {
      if (params.row.isDetailRow) {
        const product = params.row.product as Product;
        return (
          <WarehouseInventoryPanel
            id={`warehouse-inventory-panel-${getProductId(product)}`}
            open={params.row.expandState === 'open'}
            details={mapWarehouseProducts(product)}
            onRequestClose={() => toggleWarehouseDetail(product)}
            onExited={() => finalizeWarehouseCollapse(product)}
          />
        );
      }
      const product = params.row as Product;
      return (
        <Box display="flex" alignItems="center" justifyContent="center" mt="1rem">
          {product.is_kit && product.kit_components?.length ? (
            <KitThumbnailStack components={product.kit_components} />
          ) : (
            <img
              src={product.thumbnail}
              style={{ width: '50px', height: '50px', borderRadius: '1rem' }}
            />
          )}
        </Box>
      );
    },
  },
  {
    field: 'name',
    flex: 1,
    headerName: 'Descripción',
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        mt="2rem"
        width="100%"
        overflow="hidden"
      >
        <Tooltip followCursor title={params.row.name}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              color: (theme) => theme.palette.grey[600],
              span: { fontWeight: 100 },
              maxWidth: '100%',
            }}
          >
            {params.row.name}:
          </Typography>
        </Tooltip>
        <Typography
          variant="body2"
          sx={{
            color: (theme) => theme.palette.grey[600],
            fontWeight: 100,
            display: 'inline',
          }}
        >
          {' '}
          SKU {params.row.sku}
        </Typography>
        {params.row.is_kit && (
          <Chip
            label="Kit de productos"
            size="small"
            color="primary"
            sx={{
              mt: 0.5,
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 'bold',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
      </Box>
    ),
  },
  {
    field: 'value',
    flex: 0.5,
    headerName: 'Costo',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => (
      <Typography
        variant="body2"
        sx={{ color: (theme) => theme.palette.grey[600], display: 'inline' }}
      >
        ${params.row.value}
      </Typography>
    ),
  },
  {
    field: 'inventory',
    flex: 1,
    headerName: 'Inventario',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderHeader: () => (
      <Box display="flex" alignItems="center" gap={0.5}>
        <Box
          component="span"
          sx={{
            color: (theme) => theme.palette.primary.main,
            fontWeight: (theme) => theme.typography.fontWeightMedium,
          }}
        >
          Inventario
        </Box>
        <Tooltip
          title={
            <Box>
              <Typography variant="caption" display="block">
                <b>Físico:</b> Total de unidades en el almacén.
              </Typography>
              <Typography variant="caption" display="block">
                <b>Disponible:</b> Total de unidades disponibles para envío.
              </Typography>
              <Typography variant="caption" display="block">
                <b>En proceso:</b> Unidades ya vendidas, pendientes de despacho al cliente.
              </Typography>
            </Box>
          }
        >
          <InfoOutlinedIcon sx={{ fontSize: '1rem', color: (theme) => theme.palette.grey[500] }} />
        </Tooltip>
      </Box>
    ),
    renderCell: (params) => {
      if (params.row.isDetailRow) return null;
      const productId = getProductId(params.row);
      const isExpanded = expandedRows[productId] === 'open';
      const inProcess = mapWarehouseProducts(params.row).reduce(
        (sum, warehouse) => sum + warehouse.inProcess,
        0,
      );
      return (
        <Box display={'flex'} flexDirection={'column'} alignItems={'center'} mt="1rem">
          <Typography
            variant="body2"
            display={'inline'}
            sx={{
              fontWeight: 'bold',
              color: (theme) => theme.palette.grey[600],
              span: { fontWeight: 100 },
            }}
          >
            Físico: <span>{params.row.stock}</span>
          </Typography>
          <Typography
            variant="body2"
            display={'inline'}
            sx={{
              fontWeight: 'bold',
              color: (theme) => theme.palette.grey[600],
              span: { fontWeight: 100 },
            }}
          >
            Disponible: <span>{params.row.inventory}</span>
          </Typography>
          <Typography
            variant="body2"
            display={'inline'}
            sx={{
              fontWeight: 'bold',
              color: (theme) => theme.palette.grey[600],
              span: { fontWeight: 100 },
            }}
          >
            En proceso: <span>{inProcess}</span>
          </Typography>
          <ButtonBase
            className="warehouse-detail-toggle"
            onClick={() => toggleWarehouseDetail(params.row)}
            aria-expanded={isExpanded}
            aria-controls={`warehouse-inventory-panel-${productId}`}
            aria-label={
              isExpanded
                ? 'Cerrar desglose de inventario por almacén'
                : 'Ver desglose de inventario por almacén'
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              mt: 0.25,
              px: 0.5,
              borderRadius: 1,
              color: (theme) => theme.palette.primary.main,
              '&:focus-visible': {
                outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: '2px',
              },
            }}
          >
            <Typography variant="caption" fontWeight="bold">
              {isExpanded ? 'Cerrar' : 'Ver desglose por almacén'}
            </Typography>
            {isExpanded ? (
              <ExpandLessIcon sx={{ fontSize: '1rem' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: '1rem' }} />
            )}
          </ButtonBase>
        </Box>
      );
    },
  },
  {
    field: 'dimensions',
    flex: 1,
    headerName: 'Detalle del producto',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => {
      const dimensions = params.row.dimensions;
      const formatValue = (value?: number, unit?: string) =>
        value != null ? `${value} ${unit}` : 'N/D';
      const details: Array<[string, string]> = [
        ['Largo', formatValue(dimensions?.length, 'cm')],
        ['Alto', formatValue(dimensions?.height, 'cm')],
        ['Ancho', formatValue(dimensions?.width, 'cm')],
        ['Peso', formatValue(dimensions?.weight, 'g')],
      ];
      return (
        <Box display="flex" flexDirection="column" alignItems="center" mt="0.5rem">
          {details.map(([label, value]) => (
            <Typography
              key={label}
              variant="body2"
              display="inline"
              sx={{
                fontWeight: 'bold',
                color: (theme) => theme.palette.grey[600],
                span: { fontWeight: 100 },
              }}
            >
              {label}: <span>{value}</span>
            </Typography>
          ))}
        </Box>
      );
    },
  },
  {
    field: 'createdAt',
    flex: 1,
    headerName: 'Fecha de creación',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => (
      <Typography
        variant="body2"
        display={'inline'}
        sx={{ fontWeight: 'bold', color: (theme) => theme.palette.grey[600] }}
      >
        {formatDate(params.row.createdAt, 'dd/MM/yyyy')}
      </Typography>
    ),
  },
  {
    field: 'actions',
    flex: 0.5,
    headerName: 'Acciones',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    resizable: false,
    renderCell: (params) => {
      const isKit = Boolean(params.row.is_kit);
      return (
        <Box>
          {isKit && (
            <Tooltip followCursor title="Ver composición del Kit">
              <IconButton color="primary" onClick={() => handleViewKitComposition(params.row)}>
                <CategoryIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isKit && (
            <Tooltip followCursor title="Editar producto">
              <IconButton color="primary" onClick={() => handleEditProduct(params.row)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    },
  },
];
